/**
 * Helper para conectar navegación con performance monitor
 * Mide el tiempo que tarda cada navegación
 */

import { useEffect, useRef } from 'react';
import { navigationRef } from '../navigation/hooks/RootNavigation';
import productionLogger from '../../utils/productionLogger';

let performanceMonitorCallback = null;

/**
 * Registra el callback del monitor de performance
 * @param {Function} callback - Función para actualizar métricas (updateNavigationTime)
 */
export const registerNavigationCallback = (callback) => {
  performanceMonitorCallback = callback;
};

/**
 * Hook que mide tiempos de navegación
 */
export const useNavigationPerformance = () => {
  const navigationStartTime = useRef(null);

  useEffect(() => {
    if (!navigationRef.current) return;

    // Listener cuando comienza navegación
    const unsubscribeStateChange = navigationRef.addListener('state', (e) => {
      // La navegación ha terminado, medir tiempo
      if (navigationStartTime.current) {
        const duration = Date.now() - navigationStartTime.current;
        
        // Notificar al monitor
        if (performanceMonitorCallback && typeof performanceMonitorCallback === 'function') {
          performanceMonitorCallback(duration);
        }

        // Log en producción
        const currentRoute = navigationRef.getCurrentRoute();
        if (currentRoute) {
          productionLogger.logNavigation(currentRoute.name, currentRoute.params, duration);
        }

        if (__DEV__) {
          console.log(`[Navigation Performance] ${currentRoute?.name}: ${duration}ms`);
        }

        navigationStartTime.current = null;
      }
    });

    // Listener cuando se presiona para navegar
    const unsubscribeBeforeRemove = navigationRef.addListener('__unsafe_action__', () => {
      navigationStartTime.current = Date.now();
    });

    return () => {
      unsubscribeStateChange();
      unsubscribeBeforeRemove();
    };
  }, []);
};

export default {
  registerNavigationCallback,
  useNavigationPerformance,
};
